<template>
	<div class="resource-planning-user-select-dropdown">
		<!-- Loading data -->
		<div
			v-if="isLoading"
			class="resource-planning-user-select-dropdown__trigger"
		>
			<SuiSkeleton is-circle width="3.5rem" />
		</div>
		<!-- Role placeholder avatar (non-interactive) for role booking when editing disabled -->
		<div
			v-else-if="showRoleAvatarNonInteractive"
			v-tooltip="selectedEntityName"
			class="resource-planning-user-select-dropdown__trigger"
		>
			<SuiAvatar
				:aria-label="selectedEntityName"
				size="x-small"
				:initials="
					selectedRoleInitials || getInitials(selectedRoleName)
				"
				:color="selectedRoleColor"
				show-border
				variant="dashed"
			/>
		</div>
		<!-- Role placeholder avatar (interactive) for role booking when editing enabled -->
		<button
			v-else-if="showRoleAvatarInteractive"
			v-tooltip="$t('resource-booking__move-booking')"
			type="button"
			class="resource-planning-user-select-dropdown__trigger resource-planning-user-select-dropdown__trigger--interactive"
			:aria-label="$t('resource-booking__move-booking')"
			@click="toggleIsUserSelectOpen()"
		>
			<SuiAvatar
				:aria-label="selectedEntityName"
				size="x-small"
				:initials="
					selectedRoleInitials || getInitials(selectedRoleName)
				"
				:color="selectedRoleColor"
				show-border
				variant="dashed"
			/>
			<SuiIcon name="icon-arrow-down-16" />
		</button>
		<button
			v-else-if="showAddPersonIcon"
			v-tooltip="$t('user_assign_select_add_user')"
			type="button"
			class="resource-planning-user-select-dropdown__trigger resource-planning-user-select-dropdown__trigger--interactive"
			:aria-label="$t('user_assign_select_add_user')"
			@click="toggleIsUserSelectOpen()"
		>
			<SuiAvatar size="x-small" :color="PLACEHOLDER_BG_COLOR">
				<SuiIcon name="icon-addperson-16" />
			</SuiAvatar>
			<SuiIcon name="icon-arrow-down-16" />
		</button>
		<!-- Placeholder avatar for new user booking -->
		<div
			v-else-if="selectedUser && !isUserSelectionEnabled"
			v-tooltip="selectedUser.name"
			class="resource-planning-user-select-dropdown__trigger"
		>
			<SuiAvatar
				:url="selectedUser.profilePicture?.mini"
				:initials="selectedUser.initials || ''"
				:color="selectedUser.hslColor || ''"
				:aria-label="selectedUser.name"
				size="x-small"
				show-border
			/>
		</div>
		<!-- Avatar for new user booking -->
		<button
			v-else-if="selectedUser"
			v-tooltip="$t('resource-booking__move-booking')"
			type="button"
			class="resource-planning-user-select-dropdown__trigger resource-planning-user-select-dropdown__trigger--interactive"
			:aria-label="$t('resource-booking__move-booking')"
			@click="toggleIsUserSelectOpen()"
		>
			<SuiAvatar
				:url="selectedUser.profilePicture?.mini"
				:initials="selectedUser.initials || ''"
				:color="selectedUser.hslColor || ''"
				size="x-small"
				show-border
			/>
			<SuiIcon name="icon-arrow-down-16" />
		</button>
		<VDropdown
			:shown="isUserSelectOpen"
			placement="bottom-start"
			auto-hide
			:triggers="[]"
			@auto-hide="onUserSelectClose"
		>
			<template #popper>
				<ResourcePlanningUserSelect
					:title="userSelectTitle"
					:exclude-user-id="
						selectedEntityType === TYPES.RoleProject
							? null
							: selectedUser?.id
					"
					:with-role-placeholders="withRolePlaceholders"
					:project-id="projectId"
					:selected-entity-id="selectedEntityId"
					:selected-entity-type="selectedEntityType"
					@user-selected="$emit('user-selected', $event)"
					@role-selected="onRoleSelected"
					@close="onUserSelectClose"
				/>
			</template>
		</VDropdown>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { SuiIcon, SuiAvatar, SuiSkeleton } from '@developers/scoro-ui';
import { USER_LIST_USERS_INPUT, ROLE_LIST_QUERY_VARIABLES } from './constants';
import { PLACEHOLDER_BG_COLOR } from '../../constants';
import { useToggle } from '@vueuse/core';
import ResourcePlanningUserSelect from './ResourcePlanningUserSelect.vue';
import {
	useResourcePlanningUserSelectQuery,
	useResourcePlanningUserSelectRolesQuery,
	useResourcePlanningUserSelectProjectRolesQuery,
	type User,
	type Role,
	type RoleProject,
	type Project,
	type ResourcePlanningUserSelectRolesQuery,
	TYPES,
} from '@/generated/graphql';
import { useTranslation } from '@/composables';
import { getInitials } from '@/features/User/utils';

export type ResourcePlanningUserSelectDropdownProps = {
	isUserSelectionEnabled?: boolean;
	selectedEntityId?: User['id'] | Role['id'] | RoleProject['id'] | null;
	selectedEntityType?: typeof TYPES.User | typeof TYPES.RoleProject;
	selectedEntityName?: Role['name'];
	selectedRoleId?: Role['id'] | null;
	withRolePlaceholders?: boolean;
	projectId?: Project['id'];
};

const props = defineProps<ResourcePlanningUserSelectDropdownProps>();
const emit = defineEmits<{
	(event: 'user-selected', id: User['id']): void;
	(
		event: 'role-selected',
		id: Role['id'],
		roleProjectId: RoleProject['id'] | undefined
	): void;
	(event: 'role-initialized', roleId: Role['id'] | null): void;
	(event: 'close'): void;
}>();

const { t } = useTranslation();
const { selectedEntityId, selectedEntityType } = toRefs(props);
const isOpen = ref(false);
const [isUserSelectOpen, toggleIsUserSelectOpen] = useToggle(isOpen.value);
const selectedUserId = computed(() =>
	selectedEntityType.value === TYPES.User ? selectedEntityId.value : null
);

const onUserSelectClose = () => {
	emit('close');
	isUserSelectOpen.value = false;
};

const onRoleSelected = (
	roleId: Role['id'],
	roleProjectId: RoleProject['id'] | undefined
) => {
	emit('role-selected', roleId, roleProjectId);
};

const { result, loading } = useResourcePlanningUserSelectQuery({
	usersListInput: USER_LIST_USERS_INPUT,
});

// Query ALL roles (unfiltered) - for getting display data (initials, colors)
const { result: rolesResult, loading: loadingRoles } =
	useResourcePlanningUserSelectRolesQuery({
		...ROLE_LIST_QUERY_VARIABLES,
	});

// Query project roles to get roleId from roleProjectId
const { onResult: onRoleProjectResult } =
	useResourcePlanningUserSelectProjectRolesQuery(
		() => ({
			projectInput: {
				projectId: Number(props.projectId),
			},
		}),
		{
			enabled: computed(() =>
				Boolean(
					props.selectedEntityType === TYPES.RoleProject &&
					props.selectedEntityId &&
					props.projectId
				)
			),
			fetchPolicy: 'network-only',
		}
	);

onRoleProjectResult((res) => {
	if (!res.data) {
		return;
	}

	if (
		props.selectedEntityType !== TYPES.RoleProject ||
		!props.selectedEntityId
	) {
		return;
	}

	const roleProject = res.data.Project?.roleProjects?.find(
		(rp) => rp.id === props.selectedEntityId
	);

	emit('role-initialized', roleProject?.role.id ?? null);
});

const isLoading = computed(() => {
	const baseLoading = loading.value || loadingRoles.value;
	const waitingForRoleId =
		props.selectedEntityType === TYPES.RoleProject && !props.selectedRoleId;
	return baseLoading || waitingForRoleId;
});

const selectedUser = computed(() =>
	result.value?.UsersList.find((user) => user.id === selectedUserId.value)
);

const userSelectTitle = computed(() =>
	props.selectedEntityType === TYPES.User
		? t('resource-booking__move-booking')
		: t('resource-booking__assign-member')
);

const selectedRole = computed<
	ResourcePlanningUserSelectRolesQuery['RolesList'][number] | null
>(() => {
	if (!rolesResult.value?.RolesList || !isRoleType.value) {
		return null;
	}

	const roleId =
		props.selectedEntityType === TYPES.RoleProject
			? props.selectedRoleId
			: props.selectedEntityId;

	if (!roleId) {
		return null;
	}

	return rolesResult.value.RolesList.find(({ id }) => id === roleId) ?? null;
});

const selectedRoleColor = computed(
	() => selectedRole.value?.hslColor ?? undefined
);

const selectedRoleInitials = computed(
	() => selectedRole.value?.initials ?? undefined
);

const selectedRoleName = computed(
	() => selectedRole.value?.name ?? props.selectedEntityName ?? ''
);

const hasRoleData = computed(() =>
	Boolean(
		selectedRoleColor.value ||
		selectedRoleInitials.value ||
		props.selectedEntityName
	)
);

const isRoleType = computed(
	() => props.selectedEntityType === TYPES.RoleProject
);

const showRoleAvatarNonInteractive = computed(
	() => !props.isUserSelectionEnabled && isRoleType.value && hasRoleData.value
);

const showRoleAvatarInteractive = computed(
	() => props.isUserSelectionEnabled && isRoleType.value && hasRoleData.value
);

const showAddPersonIcon = computed(
	() => props.isUserSelectionEnabled && isRoleType.value && !hasRoleData.value
);
</script>

<style lang="scss" scoped>
.resource-planning-user-select-dropdown {
	&__trigger {
		display: inline-flex;
		vertical-align: top;
		align-items: center;
		padding: 0.25rem 0.5rem 0.25rem 0.25rem;
		background: $neutral-10;
		border-radius: 12.5rem;

		&--interactive {
			cursor: pointer;

			&:hover {
				background: $neutral-20;
			}
		}
	}
}
</style>
